import { useForumType } from '@/components/hooks/useForumType';
import React from 'react';
import Button from '@/lib/vendor/@material-ui/core/src/Button';
import classNames from 'classnames';
import { useCurrentUser } from "../../common/withUser";
import { isFriendlyUI } from '../../../themes/forumTheme';
import { EditorContext } from '../EditorContext';
import { useNavigate } from '../../../lib/routeUtil';
import type { TypedFormApi } from '../../tanstack-form-components/BaseAppForm';
import type { EditablePost } from '@/lib/collections/posts/helpers';
import { defineStyles, useStyles } from '../../hooks/useStyles';
import Row from "../../common/Row";
import { useMutation } from "@apollo/client/react";
import { gql } from "@/lib/generated/gql-codegen";

const CommentEditMutation = gql(`
  mutation createCommentDialogueSubmit($data: CreateCommentDataInput!) {
    createComment(data: $data) {
      data {
        ...CommentEdit
      }
    }
  }
`);

export const styles = defineStyles('DialogueSubmit', (theme: ThemeType) => ({
  formButton: {
    fontFamily: theme.typography.commentStyle.fontFamily,
    fontSize: 16,
    marginLeft: 5,
    paddingBottom: 4,
    fontWeight: 500,
    "&:hover": {
      background: theme.palette.buttons.hoverGrayHighlight,
    }
  },
  secondaryButton: {
    color: theme.palette.text.dim40,
  },
  submitButton: {
    color: theme.palette.secondary.main
  },
}));

export type DialogueSubmitProps = {
  formApi: TypedFormApi<EditablePost, {}>;
  disabled: boolean;
  submitLabel?: string;
  saveDraftLabel?: string;
};

export const DialogueSubmit = ({
  formApi,
  disabled,
  submitLabel = "Submit",
  saveDraftLabel = "Save as draft",
}: DialogueSubmitProps) => {
  const { isLW } = useForumType();
  const classes = useStyles(styles);
  const currentUser = useCurrentUser();
  if (!currentUser) throw Error("must be logged in to post")

  const [createShortform, { loading, error }] = useMutation(CommentEditMutation);
  const userShortformId = currentUser?.shortformFeedId;
  const [editor, _] = React.useContext(EditorContext)

  const navigate = useNavigate();

  const document = formApi.state.values;

  const submitWithConfirmation = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (confirm('Warning!  This will publish your dialogue and make it visible to other users.')) {
      formApi.setFieldValue('draft', false);
      await formApi.handleSubmit();
    }
  };

  const submitWithoutConfirmation = () => formApi.setFieldValue('draft', false);

  const requireConfirmation = isLW && !!document.debate;
  const showShortformButton = !!userShortformId && !isFriendlyUI();

  const onSubmitClick = requireConfirmation ? submitWithConfirmation : submitWithoutConfirmation;
  return (
    <Row justifyContent="flex-end">
      <Button type="submit"
        className={classNames(classes.formButton, classes.secondaryButton)}
        onClick={() => formApi.setFieldValue('draft', true)}
      >
        {saveDraftLabel}
      </Button>
      {showShortformButton && <Button
        className={classNames(classes.formButton)}
        disabled={loading || !!error}
        onClick={async e => {
          e.preventDefault()

          // So getData() does exist on the Editor. But the typings don't agree. For now, #AnyBecauseHard
          // @ts-ignore
          const shortformString = editor && editor.getData()
          const shortformContents = {originalContents: {type: "ckEditorMarkup", data: shortformString}}

          const response = await createShortform({
            variables: {
              data: {
                contents: shortformContents,
                shortform: true,
                postId: userShortformId,
                originalDialogueId: document._id,
              }
            }
          })

          const createdComment = response.data?.createComment?.data;

          if (createdComment) {
            navigate(`/posts/${userShortformId}?commentId=${createdComment._id}`)
          }
        }}
      >{loading ? "Publishing to shortform ..." : `Publish to ${currentUser?.displayName}'s shortform`}</Button>
      }
      <Button
        type="submit"
        onClick={onSubmitClick}
        disabled={disabled}
        className={classNames("primary-form-submit-button", classes.formButton, classes.submitButton)}
        {...(isFriendlyUI() ? {
          variant: "contained",
          color: "primary",
        } : {})}
      >
        {submitLabel}
      </Button>
    </Row>
  );
}

